/**
 * The strike layer: when a blow is live, and what it hits.
 *
 * A strike is not a moment, it is a window inside an animation. The arm winds
 * up, the fist is dangerous for a slice of the clip, and then it comes back.
 * This module owns that window and nothing else — it holds no bones, no
 * meshes and no three.js. It is fed sphere positions as plain `{x, y, z,
 * radius}` and it reports hits.
 *
 * Moves are data, in `moves.json`, and **the frame data is the authority**. A
 * move declares `startup`, `active` and `recovery` in frames at the file's own
 * `frameRate`, and that is what decides how long the move takes. The animation
 * is then stretched or squeezed to fit it — `timeScale` is derived, never
 * declared.
 *
 * That inversion is the whole point of this step. Before it, the clip owned the
 * timing and each move named a fraction of the clip to be dangerous in, which
 * meant the placeholder animation was quietly deciding the balance of the game.
 * Now a move says "thirteen frames of startup" and means it, whatever clip
 * happens to be playing underneath — so a real animation can drop in later
 * without a single number changing.
 *
 * The active window is still tested as a *fraction* internally, and it still
 * stays welded to the pose for the same reason as before: the clip is scaled to
 * the move's length, so the fraction and the frame count describe the same
 * instant.
 *
 * A move also declares its `reach`: the greatest separation, centre to centre,
 * at which it still connects. That number is *measured on the rig*, not chosen —
 * it is a fact about where the clip drives the fist and how big the volumes on
 * either end are, so it is written down here for anyone who needs to reason
 * about spacing without running the swing.
 *
 * And its `state`: the state machine state the move runs in. Which states it
 * can be *launched* from is already data too, in the `to` lists of
 * `states.json`, so it is not repeated here.
 *
 * Rules the layer enforces:
 *
 *   - One strike at a time. A second `start()` while a blow is out is refused,
 *     so a move cannot be re-triggered mid-swing.
 *   - One hit per target per strike. A fist that lingers inside a torso for
 *     four frames lands once, not four times.
 *   - The window is tested as an *interval*, not as an instant. A long frame
 *     that steps clean over a 90ms window still lands, instead of the hit
 *     silently evaporating on a slow machine.
 */

export class StrikeConfigError extends Error {}

/**
 * The parts of a move that describe **throwing** it, rather than landing it.
 *
 * Everything else a move declares is a property of the *blow* and rides it
 * downstream. That inversion is the point: `blowFrom` keeps a move's flags by
 * construction, and this is the short list of exceptions — the animation it
 * plays, the frames it takes, the limb it swings, the range it covers, the
 * routes it opens, and the name on the button.
 *
 * It was the other way round until step 43, and the other way round is a trap.
 * A hit event assembled from a hand-picked list silently drops any flag nobody
 * remembered to add, which is how `unblockable` came to have a passing test and
 * a game that ignored it. Adding a field to *this* list is now the deliberate
 * act; adding a flag to a move is the free one.
 */
export const THROWN_WITH = Object.freeze([
  'clip',
  'state',
  'hitbox',
  'startup',
  'active',
  'recovery',
  'reach',
  'cancel',
  'follows',
  'name',
]);

/**
 * A blow, described — built **from the move** rather than from a list of the
 * fields somebody thought to copy across.
 *
 * `extra` is what the collision knows and the move does not: who was hit, where,
 * how much of the blow that part actually takes, and the point it landed at. It
 * is applied last, so anything the world measured beats anything the move
 * declared — `damage` in particular, which is scaled by the part.
 *
 * Grapple fields (`hold`, `escape`, `partnerOffset` and the rest) ride along
 * inert. That is deliberate: a second exclusion list that has to be kept in step
 * is the thing this function exists to stop.
 */
export function blowFrom(move, extra = {}) {
  const blow = {};
  for (const [key, value] of Object.entries(move ?? {})) {
    if (THROWN_WITH.includes(key)) continue;
    blow[key] = value;
  }
  return {
    ...blow,
    // Three defaults, because a blow is always asked these and a move need not
    // declare them.
    kind: move?.kind ?? 'strike',
    tags: move?.tags ?? [],
    /** Nothing follows it, so it is the last blow of whatever chain it is in. */
    ender: (move?.follows ?? []).length === 0,
    ...extra,
  };
}

/** A hit is `normal` or `strong`; the difference is what it can interrupt. */
export const STRENGTHS = Object.freeze(['normal', 'strong']);

/** Used when the caller cannot tell us how long the clip actually is. */
const FALLBACK_CLIP = 0.7;

const PHASES = Object.freeze(['startup', 'active', 'recovery']);

/**
 * What a move *is*.
 *
 * A `strike` connects and does damage. A `grapple` connects and starts a paired
 * state instead — the frame data, the hitbox and the active window are all the
 * same shape, because a clinch is thrown the same way a punch is. What differs
 * is entirely what happens once it lands, and that is not this module's
 * business.
 */
export const KINDS = Object.freeze(['strike', 'grapple']);

/** Extra fields a grapple has to carry, on top of everything a strike needs. */
function validateGrapple(name, move, knownStates) {
  for (const field of ['holdState', 'partnerState']) {
    if (!move[field]) throw new StrikeConfigError(`grapple "${name}" has no ${field}`);
    if (knownStates && !knownStates.includes(move[field])) {
      throw new StrikeConfigError(`grapple "${name}" names unknown state "${move[field]}"`);
    }
  }
  const offset = move.partnerOffset;
  if (!Array.isArray(offset) || offset.length !== 3 || offset.some((n) => !Number.isFinite(n))) {
    throw new StrikeConfigError(`grapple "${name}" needs a [x, y, z] partnerOffset`);
  }
  if (!(move.hold > 0)) {
    throw new StrikeConfigError(`grapple "${name}" must hold for a positive number of frames`);
  }
  // `escape: null` is a real answer, not a missing one: a hold nobody gets out
  // of. It is how a finisher on a fighter who is already reeling differs from a
  // clinch on one who is not.
  const escape = move.escape;
  if (escape === null || escape === undefined) return;
  if (!(escape.open >= 0) || !(escape.frames > 0)) {
    throw new StrikeConfigError(`grapple "${name}" has a malformed escape window`);
  }
  if (escape.open + escape.frames > move.hold) {
    // An escape window that outlasts the hold can never be missed, which makes
    // it a button rather than a window.
    throw new StrikeConfigError(`grapple "${name}" escape window outlasts the hold`);
  }
}

/**
 * A move's shape, in frames and as fractions of itself.
 *
 * `open` and `close` are where the active window sits inside the move as a
 * 0..1 fraction, which is what the interval test needs, and what the animation
 * is scaled against.
 */
export function timing(move) {
  const total = move.startup + move.active + move.recovery;
  return {
    frames: total,
    open: move.startup / total,
    close: (move.startup + move.active) / total,
  };
}

/** Throws on a move set that cannot be satisfied, so a typo fails at boot. */
export function validateMoves(config, { knownStates } = {}) {
  const { frameRate, moves } = config ?? {};
  if (!(frameRate > 0)) {
    throw new StrikeConfigError(`move set has a frameRate of ${frameRate}`);
  }
  if (!moves || typeof moves !== 'object') {
    throw new StrikeConfigError('move set has no "moves" object');
  }

  for (const [name, move] of Object.entries(moves)) {
    if (!move.name) throw new StrikeConfigError(`move "${name}" has no display name`);
    if (!move.clip) throw new StrikeConfigError(`move "${name}" has no clip`);
    if (!move.hitbox) throw new StrikeConfigError(`move "${name}" has no hitbox`);
    if (!move.state) throw new StrikeConfigError(`move "${name}" has no state`);
    // A move that runs in a state the graph has never heard of would enter
    // nothing and lock the fighter up, at the moment it was first thrown.
    if (knownStates && !knownStates.includes(move.state)) {
      throw new StrikeConfigError(`move "${name}" runs in unknown state "${move.state}"`);
    }
    if (!STRENGTHS.includes(move.strength)) {
      throw new StrikeConfigError(
        `move "${name}" has strength "${move.strength}"; expected ${STRENGTHS.join(' or ')}`
      );
    }
    if (!(move.damage > 0)) {
      throw new StrikeConfigError(`move "${name}" must do positive damage`);
    }
    if (!(move.reach > 0)) {
      throw new StrikeConfigError(`move "${name}" must declare a positive reach`);
    }
    for (const phase of PHASES) {
      const frames = move[phase];
      if (!Number.isInteger(frames) || frames < 0) {
        throw new StrikeConfigError(`move "${name}" has ${phase} of ${frames}; want whole frames`);
      }
    }
    // A move with no active frames is dangerous for no part of itself.
    if (!(move.active > 0)) {
      throw new StrikeConfigError(`move "${name}" has no active frames`);
    }
    if (move.knockback !== undefined && !(move.knockback >= 0)) {
      throw new StrikeConfigError(`move "${name}" has a knockback of ${move.knockback}`);
    }
    // A flag, not a value: `requiresStun: "yes"` would be truthy and would work
    // by accident, which is how a typo becomes a rule.
    if (move.requiresStun !== undefined && typeof move.requiresStun !== 'boolean') {
      throw new StrikeConfigError(
        `move "${name}" has a requiresStun of ${JSON.stringify(move.requiresStun)}; want true or false`
      );
    }
    if (move.kind !== undefined && !KINDS.includes(move.kind)) {
      throw new StrikeConfigError(
        `move "${name}" is a "${move.kind}"; expected ${KINDS.join(' or ')}`
      );
    }
    if (move.kind === 'grapple') validateGrapple(name, move, knownStates);
  }
  return config;
}

export function createStrikeLayer(config, { onHit, onWhiff, knownStates } = {}) {
  validateMoves(config, { knownStates });

  const { frameRate } = config;
  const moves = config.moves;
  /**
   * The moves as this body throws them. `moves.json` says how far a blow
   * reaches on the mannequin it was measured on; a body's limbs scale that
   * (`setBodyReach`). Only the *reading* changes — what lands is decided by
   * spheres on the real bones, which already reach as far as the body does.
   * Weapon moves are declared, not measured (§7), and are left as they are.
   */
  let thrown = moves;

  let name = null;
  let move = null;
  let shape = null;
  let elapsed = 0;
  let previous = 0;
  let duration = 0;
  /** What the mixer must do to the clip to make it last exactly this move. */
  let clipScale = 1;
  /** Whether this strike has already been declared a miss. */
  let missed = false;
  /** Targets already hit by the strike currently out. Cleared on each start. */
  const landed = new Set();
  const listeners = new Set();
  const whiffListeners = new Set();
  if (onHit) listeners.add(onHit);
  if (onWhiff) whiffListeners.add(onWhiff);

  function progress(at = elapsed) {
    return duration > 0 ? Math.min(at / duration, 1) : 1;
  }

  /**
   * True when the frame just stepped through any part of the window. Testing
   * the span [previous, elapsed] rather than the instant `elapsed` is what
   * stops a dropped frame from stepping over a short window.
   */
  function windowOpen() {
    if (!move) return false;
    return progress(previous) <= shape.close && progress(elapsed) >= shape.open;
  }

  function phase() {
    if (!move) return 'idle';
    const p = progress();
    if (p < shape.open) return 'startup';
    if (p <= shape.close) return 'active';
    return 'recovery';
  }

  function end() {
    name = null;
    move = null;
    shape = null;
    elapsed = 0;
    previous = 0;
    duration = 0;
    clipScale = 1;
    missed = false;
    landed.clear();
  }

  function emit(hit) {
    for (const listener of listeners) listener(hit);
  }

  /**
   * The deepest overlap wins, rather than the first one found. A punch aimed
   * at the chest often clips the defender's hanging forearm on the way in;
   * picking by penetration depth means the torso takes it, which is what the
   * eye saw happen.
   */
  function deepestOverlap(hitbox, hurtboxes) {
    let best = null;
    for (const box of hurtboxes ?? []) {
      const reach = hitbox.radius + box.radius;
      const dx = box.x - hitbox.x;
      const dy = box.y - hitbox.y;
      const dz = box.z - hitbox.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (distance > reach) continue;
      const depth = reach - distance;
      if (!best || depth > best.depth) best = { box, depth, distance };
    }
    return best;
  }

  function resolve({ hitboxes, targets } = {}) {
    const hitbox = hitboxes?.get?.(move.hitbox);
    if (!hitbox) return;

    for (const target of targets ?? []) {
      if (landed.has(target.id)) continue;
      const overlap = deepestOverlap(hitbox, target.hurtboxes);
      if (!overlap) continue;

      landed.add(target.id);
      const { box, depth } = overlap;
      // Every flag the move declares rides across by construction. What is
      // spelled out here is only what the *collision* knows and the move cannot:
      // who was hit, where, and how much of the blow that part actually takes —
      // a head is not a thigh.
      emit(
        blowFrom(move, {
          strike: name,
          target: target.id,
          part: box.name,
          damage: move.damage * (box.damageScale ?? 1),
          consciousnessMultiplier: box.consciousnessMultiplier,
          depth,
          point: {
            x: (hitbox.x + box.x) / 2,
            y: (hitbox.y + box.y) / 2,
            z: (hitbox.z + box.z) / 2,
          },
        })
      );
    }
  }

  return {
    names: () => Object.keys(moves),
    get: (which) => thrown[which] ?? null,
    /**
     * The body throwing these: `{ leftHand, rightHand, leftFoot, rightFoot }`
     * as multiples of the mannequin's reach. Null puts the measured numbers back.
     */
    setBodyReach(scale) {
      thrown = moves;
      if (!scale) return;
      thrown = {};
      for (const [id, m] of Object.entries(moves)) {
        const by = m.weapon ? 1 : (scale[m.hitbox] ?? 1);
        thrown[id] = by === 1 ? m : Object.freeze({ ...m, reach: Math.round(m.reach * by * 1000) / 1000 });
      }
    },
    /** A move's shape in frames, for anything that needs to reason about it. */
    timing: (which) => (moves[which] ? timing(moves[which]) : null),
    frameRate,

    get active() {
      return move !== null;
    },
    get strike() {
      return name;
    },
    get strength() {
      return move?.strength ?? null;
    },
    get clip() {
      return move?.clip ?? null;
    },
    /**
     * What the mixer must do to the clip so it lasts exactly as long as the
     * move's frame data says the move does. Derived on `start`, never declared.
     */
    get timeScale() {
      return clipScale;
    },
    /** The live move's shape, or null. */
    get shape() {
      return shape;
    },
    get state() {
      return move?.state ?? null;
    },
    get duration() {
      return duration;
    },
    get progress() {
      return move ? progress() : 0;
    },
    get phase() {
      return phase();
    },
    get windowOpen() {
      return windowOpen();
    },
    /** Targets this strike has already connected with. */
    get landed() {
      return [...landed];
    },
    /** True once this strike's window has shut without touching anybody. */
    get missed() {
      return missed;
    },

    /**
     * Throws `which`.
     *
     * The move's own frame data sets how long it runs. `clipDuration` is the
     * unscaled length of whatever animation is standing in for it, and is used
     * only to work out what to do to the mixer so the pose keeps up — the
     * animation is fitted to the move, not the other way round.
     *
     * Refuses an unknown move, and refuses to start on top of a live strike.
     */
    start(which, { clipDuration, speed = 1 } = {}) {
      if (!moves[which] || move) return false;
      name = which;
      move = moves[which];
      shape = timing(move);
      // Speed scales the whole move, so startup, active and recovery all shrink
      // together and the active window stays welded to the same fraction of the
      // pose. A faster style is not a style with a different move.
      duration = shape.frames / frameRate / (speed > 0 ? speed : 1);
      clipScale = (clipDuration || FALLBACK_CLIP) / duration;
      elapsed = 0;
      previous = 0;
      missed = false;
      landed.clear();
      return true;
    },

    /**
     * Advances the strike and resolves it against `targets`. Pass the context
     * *after* the skeleton has been posed for this frame — the hitbox reads
     * from the bone, so a stale pose resolves last frame's punch.
     */
    update(dt, context) {
      if (!move || !(dt > 0)) return;
      previous = elapsed;
      elapsed += dt;
      if (context && windowOpen()) resolve(context);
      // A listener can end the move from inside `resolve` — a clinch that
      // connects takes its thrower straight into a hold, and leaving the
      // move's state cancels the layer. Everything below reads `shape`, which
      // is gone by then.
      if (!move) return;

      // The instant the active window shuts with nothing hit, the strike has
      // definitively missed — a blow only ever resolves inside its window, so
      // there is nothing left that could still land.
      //
      // Announced *here* rather than when the strike ends, because the whole
      // point of a whiff is the recovery you are stuck in. Waiting for the end
      // of the clip would start the punish window at the moment the fighter
      // could act again, which is the one moment it does not matter.
      if (!missed && landed.size === 0 && progress() > shape.close) {
        missed = true;
        for (const listener of whiffListeners) {
          listener({ strike: name, strength: move.strength });
        }
      }

      if (elapsed >= duration) end();
    },

    /** Drops the strike without resolving it — a staggered fighter's punch. */
    cancel() {
      if (move) end();
    },

    onHit(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * Fires once per strike whose window shut with nothing hit.
     *
     * Being knocked out of a punch does *not* count: `cancel()` drops the
     * strike without announcing anything, because eating a blow mid-swing is
     * already the punishment and stacking a second one on top of it would
     * punish the same mistake twice.
     */
    onWhiff(listener) {
      whiffListeners.add(listener);
      return () => whiffListeners.delete(listener);
    },
  };
}
